import { useCallback, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets'
import { useCreateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { ChannelListItem } from './ChannelListItem'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { buildChannelRows } from './buildChannelRows'
import Styles from './ChannelList.module.scss'

interface ChannelListProps {
  crossPostGroup: readonly ExternalIntegrationEnum[]
  almanacFile: AlmanacFileShowItem
  activeProvider?: ExternalIntegrationEnum
  activeIntegrationId?: number
  onSelectPlatform: (provider: ExternalIntegrationEnum, integrationId?: number) => void
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
}

const findTarget = (
  targets: ScheduledPostTarget[],
  provider: ExternalIntegrationEnum,
  integrationId: number | undefined,
) =>
  integrationId
    ? targets.find((t) => t.provider === provider && t.external_integration_id === integrationId)
    : targets.find((t) => t.provider === provider)

export const ChannelList = ({
  crossPostGroup,
  almanacFile,
  activeProvider,
  activeIntegrationId,
  onSelectPlatform,
  onUpdateAlmanacFile,
}: ChannelListProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { data: integrationsData } = useExternalIntegrations()
  const { data: targets = [] } = useScheduledPostTargets(almanacFile.scheduled_post?.id ?? 0, {
    enabled: !!almanacFile.scheduled_post?.id,
  })
  const { disconnectedIntegrations } = useIntegrationHeartbeatStatus()

  const scheduledPostId = almanacFile.scheduled_post?.id
  const isPosted = almanacFile.post_status === PostStatusPropertyValue.Posted
  const channel = almanacFile.channel ?? undefined

  const createMutation = useCreateScheduledPostTarget()
  const deleteMutation = useDeleteScheduledPostTarget()
  const { mutate: publishTargets } = usePublishScheduledPostTargets()
  const { initiateOauth } = useInitiateOauth()
  const { connect: connectAccountPopup } = useConnectAccountPopup()

  const integrations = useMemo(() => integrationsData?.external_integrations ?? [], [integrationsData])

  const accountIdsNeedReauth = useMemo(() => disconnectedIntegrations.map((i) => i.id), [disconnectedIntegrations])

  const rows = useMemo(
    () =>
      buildChannelRows({
        crossPostGroup,
        integrations,
        targets,
        accountIdsNeedReauth,
        isPosted,
        channel,
      }),
    [crossPostGroup, integrations, targets, accountIdsNeedReauth, isPosted, channel],
  )

  const handleToggle = useCallback(
    (provider: ExternalIntegrationEnum, integrationId: number | undefined, isChecked: boolean) => {
      if (!scheduledPostId) return
      const integration = integrationId ? integrations.find((i) => i.id === integrationId) : undefined

      if (isChecked) {
        createMutation.mutate(
          {
            scheduled_post_id: scheduledPostId,
            provider,
            target_overrides: {},
            ...(integrationId && { external_integration_id: integrationId, integration_name: integration?.name }),
          },
          {
            onSuccess: (newTargets) => {
              onUpdateAlmanacFile?.({
                ...almanacFile,
                scheduled_post_targets: newTargets,
              })
            },
          },
        )

        return
      }

      const target = findTarget(targets, provider, integrationId)
      if (!target) return
      deleteMutation.mutate(
        {
          scheduled_post_id: scheduledPostId,
          target_id: target.id,
          ...(integration && { integration_name: integration.name }),
        },
        {
          onSuccess: () => {
            onUpdateAlmanacFile?.({
              ...almanacFile,
              scheduled_post_targets: [...targets.filter((t) => t.id !== target.id)],
            })
          },
        },
      )
    },
    [scheduledPostId, integrations, targets, deleteMutation, createMutation, onUpdateAlmanacFile, almanacFile],
  )

  const handleConnect = useCallback(
    async (provider: ExternalIntegrationEnum) => {
      const popup = await initiateOauth(provider, undefined, '_blank', 'popup=true,width=600,height=780')
      connectAccountPopup(popup as Window | undefined, {
        onConnect: () => {
          queryClient.invalidateQueries(calendarKeys.all)
          queryClient.invalidateQueries(['externalIntegrations', workspaceId])
        },
      })
    },
    [initiateOauth, connectAccountPopup, queryClient, workspaceId],
  )

  const handleRetry = useCallback(
    (provider: ExternalIntegrationEnum, integrationId: number | undefined) => {
      if (!scheduledPostId) return
      const target = findTarget(targets, provider, integrationId)
      if (!target) return
      publishTargets({ scheduledPostId, targetIds: [target.id] })
    },
    [scheduledPostId, targets, publishTargets],
  )

  const selectedRow = useMemo(() => {
    const providerRows = rows.filter((r) => r.provider === activeProvider)
    if (!providerRows.length) return undefined
    return providerRows.find((r) => r.integrationId === activeIntegrationId) ?? providerRows[0]
  }, [rows, activeProvider, activeIntegrationId])

  return (
    <div className={Styles.channelList}>
      {rows.map((row) => {
        const key = row.integrationId ? `${row.provider}:${row.integrationId}` : row.provider
        return (
          <ChannelListItem
            key={key}
            provider={row.provider}
            integrationId={row.integrationId}
            accountName={row.accountName}
            accountAvatarUrl={row.accountAvatarUrl}
            state={row.state}
            isSelected={row === selectedRow}
            postUrl={row.postUrl}
            expiredTooltip={row.expiredTooltip}
            onSelect={() => onSelectPlatform(row.provider, row.integrationId)}
            onToggle={(isChecked) => handleToggle(row.provider, row.integrationId, isChecked)}
            onConnect={() => handleConnect(row.provider)}
            onRetry={() => handleRetry(row.provider, row.integrationId)}
            onPostNow={() => handleRetry(row.provider, row.integrationId)}
          />
        )
      })}
    </div>
  )
}
